
import React, {useState, useEffect, useRef} from 'react';

import {
  createInstance,
  OptimizelyProvider,
  enums,
} from '@optimizely/react-sdk';
import PropTypes from 'prop-types';

import {isNonProdEnvironment} from 'utils/environment';

import {usePromiseMemo} from './usePromise';

const useOptimizelyAttributes = (user) => {
  // Initial Optimizely attributes are globally attached by the Django backend.
  const initialOptimizelyAttributes = window.initial_optimizely_attributes;

  const isInitialRender = useRef(true);
  const hasEverFinishedRequestRef = useRef(false);

  const {data: optimizelyAttributesFromServer} = usePromiseMemo(async() => {
    // Don't make a request if we already have initial attributes available and
    // it's the initial render.
    if (initialOptimizelyAttributes && isInitialRender.current) {
      return;
    }
    try {
      const response = await fetch('/api/v3/optimizely/my-attributes/', {
        credentials: 'include',
      });
      return response.json();
    } finally {
      hasEverFinishedRequestRef.current = true;
    }
  }, [user]);

  useEffect(() => {
    isInitialRender.current = false;
  }, []);

  // Return attributes from the server if we have them.
  if (optimizelyAttributesFromServer) {
    return optimizelyAttributesFromServer;
  }

  // Otherwise, return initial attributes if no request for Optimizely
  // attributes has yet been finished.
  if (!hasEverFinishedRequestRef.current) {
    return initialOptimizelyAttributes;
  }
};

const hopscotchExperimentEventConverter = (eventStreamEvent) => {
  const mappedOptimizelyEvents = [];
  const optimizelyMap = {
    'Instructional note click': [
      'Content Initiate Read click',
      // TODO: Check on 'resource panel click event names.
    ],
    'Content impression': [
      'Content Navigate impression',
    ],
    'Content click': [
      'Content Navigate click',
    ],
    'Assignment created': [
      'Assignment Initiate Create click',
      'Assignment Initiate Update click',
      'Assignment Create click',
    ],
  };

  for (const optimizelyEventKey of Object.keys(optimizelyMap)) {
    // Allow for greater specificity in event mapper by flattening to include event type.
    const flattenedEventStreamName = `${eventStreamEvent.event_name} ${eventStreamEvent.dimension_properties.event_type}`;

    if (optimizelyMap[optimizelyEventKey].includes(flattenedEventStreamName)) {
      mappedOptimizelyEvents.push(optimizelyEventKey);
    }
  }

  // Array of objects with optimizely event name and tags
  return mappedOptimizelyEvents;
};

const eventConverters = [
  hopscotchExperimentEventConverter
];

const convertToOptimizelyEventAndSend = (eventStreamEvent, config) => {
  const optimizelyEventKeysToEmit = [];

  for (const converter of eventConverters) {
    try {
      const optimizelyEventKeysForThisConverter = converter(eventStreamEvent);
      optimizelyEventKeysToEmit.push(...optimizelyEventKeysForThisConverter);
    } catch (error) {
      // TODO: log warning to sentry
      // eslint-disable-next-line
      console.error(error);
    }
  }

  try {
    for (const optimizelyEventKey of optimizelyEventKeysToEmit) {
      const optimizelyEventTags = {eventStreamId: eventStreamEvent.id};
      config.optimizely.track(optimizelyEventKey, undefined, undefined, optimizelyEventTags);
    }
  } catch (error) {
    // TODO: log warning to sentry
    // eslint-disable-next-line
    console.error(error);
  }
};

const createOptimizelyInstance = (options) => {
  return createInstance({
    sdkKey: window.OPTIMIZELY_SDK_KEY,
    // Can set log level to LOG_LEVEL.DEBUG for addt'l logs from Optimizely
    logLevel: isNonProdEnvironment() ? enums.LOG_LEVEL.INFO : enums.LOG_LEVEL.ERROR,
    ...options,
  });
};

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number
  }),
  children: PropTypes.node,
};

const NewselaOptimizelyProvider = ({user, children}) => {
  const optimizelyAttributes = useOptimizelyAttributes(user);

  const [optimizely, setOptimizely] = useState(createOptimizelyInstance);
  useEffect(() => {
    optimizely.onReady().then((result) => {
      const {success} = result;
      const optConfig = optimizely.getOptimizelyConfig();
      if (success && optConfig) return;
      // If the regular Optimizely CDN request fails, use a custom made
      // endpoint on our backend that proxies to Optimizely. This helps enable
      // Optimizely variation logic in the face of ad / content blockers. It
      // does NOT prevent such blockers from blocking decision or conversion
      // events we attempt to send to Optimizely.
      setOptimizely(createOptimizelyInstance({
        datafileOptions: {
          urlTemplate: '/api/v3/optimizely/datafiles/%s'
        },
      }));
    });
  }, []);

  return (
    <OptimizelyProvider
      optimizely={optimizely}
      user={{
        id: optimizelyAttributes?.userId,
        attributes: optimizelyAttributes
      }}
    >
      {children}
    </OptimizelyProvider>
  );
};

NewselaOptimizelyProvider.propTypes = propTypes;

export {useOptimizelyAttributes, convertToOptimizelyEventAndSend, NewselaOptimizelyProvider};
